import { Document, Page, View } from "@react-pdf/renderer";
import React from "react";

import { A4FacilitySnapshotSection } from "./sections/FacilitySnapshotSection";
import { LineChartSection } from "./sections/LineChartSection";
import { OverallAISummarySection } from "./sections/OverallAISummarySection";
import { ReviewsCommentsSection } from "./sections/ReviewsCommentsSection";
import { ReviewsSummarySection } from "./sections/ReviewsSummarySection";
import { ReviewsTimelineSection } from "./sections/ReviewsTimelineSection";
import { getLineChartMaxHeight, pageStyles } from "./styles";
import { FacilityReportData } from "../../../../../models/FacilityReportData";
import { SnapshotTranslate } from "../../FacilitySnapshotSection/utils";
import { BackCoverPage, FrontCoverPage } from "../CoverPages";

export interface IA4ChartImages {
  website?: string;
  calls?: string;
  routeSearch?: string;
  impressions?: string;
  searchKeywords?: string;
  reviewsCommentsChart?: string;
  reviewsTimeline?: string;
}

interface IA4PDFContentProps {
  orientation: "portrait" | "landscape";
  facilityReportData: FacilityReportData;
  facilityName: string;
  honorificSuffix: string;
  chartImages: IA4ChartImages;
  snapshotPostImages: (string | undefined)[];
  snapshotT: SnapshotTranslate;
  sectionTitles: {
    overallAISummary: string;
    website: string;
    calls: string;
    routeSearch: string;
    impressions: string;
    searchKeywords: string;
    noData: string;
    reviews: {
      title: string;
      summary: string;
      improvements: string;
      noImprovements: string;
      timeline: string;
      positiveComments: string;
      negativeComments: string;
      commentExample: string;
      noComments: string;
    };
  };
}

export const A4PDFContent: React.FunctionComponent<IA4PDFContentProps> = ({
  orientation,
  facilityReportData,
  facilityName,
  honorificSuffix,
  chartImages,
  snapshotPostImages,
  snapshotT,
  sectionTitles,
}) => {
  const reportData =
    facilityReportData.location_facility_reports_report_data?.data;
  const reviewSummary = reportData?.reviews?.review_summary;

  return (
    <Document>
      <FrontCoverPage
        facilityName={facilityName}
        honorificSuffix={honorificSuffix}
        pageSize="a4-landscape"
      />
      {reportData?.facility_snapshot && (
        <Page
          size="A4"
          orientation={orientation}
          style={pageStyles.page}
          wrap={false}
        >
          <A4FacilitySnapshotSection
            orientation={orientation}
            snapshot={reportData.facility_snapshot}
            postImages={snapshotPostImages}
            t={snapshotT}
          />
        </Page>
      )}
      {reportData?.impressions && (
        <Page size="A4" orientation={orientation} style={pageStyles.page}>
          <LineChartSection
            title={sectionTitles.impressions}
            aiSummary={reportData.impressions.ai_summary}
            maxChartHeight={getLineChartMaxHeight(orientation)}
            chartImage={chartImages.impressions}
            noDataLabel={sectionTitles.noData}
          />
        </Page>
      )}
      {reportData?.search_keywords && (
        <Page size="A4" orientation={orientation} style={pageStyles.page}>
          <LineChartSection
            title={sectionTitles.searchKeywords}
            aiSummary={reportData.search_keywords.ai_summary}
            maxChartHeight={getLineChartMaxHeight(orientation)}
            chartImage={chartImages.searchKeywords}
            noDataLabel={sectionTitles.noData}
          />
        </Page>
      )}
      {reportData?.website && (
        <Page size="A4" orientation={orientation} style={pageStyles.page}>
          <LineChartSection
            title={sectionTitles.website}
            aiSummary={reportData.website.ai_summary}
            maxChartHeight={getLineChartMaxHeight(orientation)}
            chartImage={chartImages.website}
            noDataLabel={sectionTitles.noData}
          />
        </Page>
      )}
      {reportData?.calls && (
        <Page size="A4" orientation={orientation} style={pageStyles.page}>
          <LineChartSection
            title={sectionTitles.calls}
            aiSummary={reportData.calls.ai_summary}
            maxChartHeight={getLineChartMaxHeight(orientation)}
            chartImage={chartImages.calls}
            noDataLabel={sectionTitles.noData}
          />
        </Page>
      )}
      {reportData?.route_search && (
        <Page size="A4" orientation={orientation} style={pageStyles.page}>
          <LineChartSection
            title={sectionTitles.routeSearch}
            aiSummary={reportData.route_search.ai_summary}
            maxChartHeight={getLineChartMaxHeight(orientation)}
            chartImage={chartImages.routeSearch}
            noDataLabel={sectionTitles.noData}
          />
        </Page>
      )}
      {reportData?.reviews && reviewSummary && (
        <>
          <Page size="A4" orientation={orientation} style={pageStyles.page}>
            <ReviewsSummarySection
              title={sectionTitles.reviews.title}
              summaryText={reviewSummary.summary?.summary}
              improvements={reviewSummary.summary?.improvements}
              commentsChartImage={chartImages.reviewsCommentsChart}
              labels={{
                summary: sectionTitles.reviews.summary,
                improvements: sectionTitles.reviews.improvements,
                noImprovements: sectionTitles.reviews.noImprovements,
                noData: sectionTitles.noData,
              }}
            />
          </Page>
          <Page size="A4" orientation={orientation} style={pageStyles.page}>
            <View
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 24,
              }}
            >
              <ReviewsTimelineSection
                title={sectionTitles.reviews.timeline}
                chartImage={chartImages.reviewsTimeline}
                noDataLabel={sectionTitles.noData}
              />
              <ReviewsCommentsSection
                positivePoints={reviewSummary.summary?.positiveComments?.points}
                negativePoints={reviewSummary.summary?.negativeComments?.points}
                labels={{
                  positiveComments: sectionTitles.reviews.positiveComments,
                  negativeComments: sectionTitles.reviews.negativeComments,
                  commentExample: sectionTitles.reviews.commentExample,
                  noComments: sectionTitles.reviews.noComments,
                }}
              />
            </View>
          </Page>
        </>
      )}
      {reportData?.overall_ai_summary && (
        <Page size="A4" orientation={orientation} style={pageStyles.page}>
          <OverallAISummarySection
            title={sectionTitles.overallAISummary}
            aiSummary={reportData.overall_ai_summary.ai_summary}
          />
        </Page>
      )}
      <BackCoverPage pageSize="a4-landscape" />
    </Document>
  );
};
